import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import Delete from '@mui/icons-material/Delete'
import Download from '@mui/icons-material/Download'
import {
  Button,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCompleteDocumentRegistration,
  useDeleteDocument,
  useGetDocuments,
} from '@/service-library/hooks/documents'
import { useGetProjects } from '@/service-library/hooks/projects'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { Project } from '@/types/projects'
import { handleDownloadOriginalDocument } from '@/utils/download-file'
import { showSuccessSnackbar, showErrorSnackbar } from '@/utils/snackbars'
import CopyIDButton from '../copy-id-button/CopyIDButton'
import { Dialog, DialogContent, DialogFooter } from '../dialog'
import ListAutocomplete from '../list-autocomplete/ListAutocomplete'

export const orphanedDocumentFilters = {
  fields__only: 'id,name',
  upload_status: 'needs_project_assignment',
  project_id__isnull: 'true',
}

type OrphanedDocumentsDialogProps = {
  overlay: OverlayState
  setShouldRefetchCount: Dispatch<SetStateAction<boolean>>
}

export default function OrphanedDocumentsDialog({
  overlay,
  setShouldRefetchCount,
}: OrphanedDocumentsDialogProps) {
  const isSuperUser = useIsSuperUser()
  const [selectedProjects, setSelectedProjects] = useState<
    Record<string, string>
  >({})

  const {
    documents,
    isPending: isPendingDocuments,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetDocuments({
    filters: {
      ...orphanedDocumentFilters,
      limit: '10',
    },
    enabled: overlay.isOpen,
  })

  const { projects, isPending: isPendingProjects } = useGetProjects({
    filters: {
      fields__only: 'id,name',
    },
  })

  const { deleteDocument } = useDeleteDocument({
    onSuccess: () => {
      showSuccessSnackbar('Document Deleted')
    },
    onError: () => {
      showErrorSnackbar('Failed to delete document. Please try again later.')
    },
  })

  const { completeRegistration } = useCompleteDocumentRegistration({
    onSuccess: () => {
      showSuccessSnackbar('Document Assignment Started')
      setShouldRefetchCount(true)
      setTimeout(() => {
        setShouldRefetchCount(false)
      }, 60000)
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to start document assignment. Please try again later.',
      )
    },
  })

  const handleSave = () => {
    overlay.close()
    completeRegistration(
      Object.entries(selectedProjects).map(([docId, projId]) => ({
        id: docId,
        project_id: projId,
      })),
    )
  }

  useEffect(() => {
    if (overlay.isOpen) {
      setSelectedProjects({})
    }
  }, [overlay.isOpen])

  return (
    <Dialog title="Unassigned Documents" {...overlay} maxWidth="md">
      {(isPendingDocuments || isPendingProjects) && (
        <Typography variant="h6" textAlign="center" sx={{ py: 6 }}>
          Loading...
        </Typography>
      )}
      <DialogContent disablePadding>
        <List dense>
          {documents.map((document) => (
            <ListItem key={document.id} sx={{ px: 3 }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Tooltip title={document.name}>
                  <Typography sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                    {document.name}
                  </Typography>
                </Tooltip>
                <ListAutocomplete<Project>
                  autoSelect={false}
                  disableClearable={false}
                  options={projects}
                  selected={
                    projects.find(
                      (project) => project.id === selectedProjects[document.id],
                    ) || null
                  }
                  setSelected={(project) => {
                    if (project) {
                      setSelectedProjects((prev) => ({
                        ...prev,
                        [document.id]: project?.id,
                      }))
                    } else if (selectedProjects[document.id]) {
                      setSelectedProjects((prev) => {
                        const updated = { ...prev }
                        delete updated[document.id]
                        return updated
                      })
                    }
                  }}
                  variant="outlined"
                  sx={{ maxWidth: 250, flexShrink: 0 }}
                />

                <Stack direction="row" spacing={1}>
                  {isSuperUser && (
                    <CopyIDButton
                      stringToCopy={document.id}
                      asIconButton
                      isSuperUser
                    />
                  )}
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleDownloadOriginalDocument(document.id)
                      }
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => deleteDocument(document.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </ListItem>
          ))}
        </List>
        {hasNextPage && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <Button
              variant="text"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading...' : 'Load More'}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="text" onClick={overlay.close}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={Object.values(selectedProjects).length === 0}
        >
          Save
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
